/**
 * `AuftragVerlaufKarte` — WP1 (Auftrags-Dashboard, 08.09.2026). Eine Karte statt drei: ersetzt
 * die bisherigen Karten „Aktivität" (`pages/event-detail.tsx`), „Versionsarchiv" und
 * „Vorgangsansicht". Formuliert selbst nichts — jede Zeile kommt fertig aus der reinen Funktion
 * `lib/auftrag-verlauf.ts::leiteAuftragsVerlaufAb`, diese Komponente ist nur noch Darstellung +
 * Klick-Weiterleitung:
 * ── A4 (13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md` Einheit A) ──────────────────────────────
 * Bis dahin war GENAU EINE Zeilenart klickbar (die Angebotsversion); alles andere war eine
 * Sackgasse, obwohl `activity_log.details` die verbindende ID längst mitliefert. Jetzt entscheidet
 * die reine Funktion `lib/auftrag-verlauf.ts::verlaufZiel`, ob eine Zeile ein Ziel hat:
 * Rechnung/Zahlung/Kostenübernahme → Abrechnungs-Unterseite, Nachricht/Mail → Posteingang,
 * Angebotsversion → weiterhin der Drawer (kein URL-Ziel), alles andere → kein Klick (statt eines
 * toten Klicks). **Jedes Seiten-Ziel ist ein echtes `<Link to=…>`, nie `onClick`+`navigate`** —
 * Cmd-/Strg-/Mittelklick muss einen neuen Tab öffnen (ausdrückliche Forderung von Antoine).
 *
 *  - Version-Zeilen sind klickbar (`data-testid="verlauf-version-${n}"`) und öffnen den
 *    bestehenden `VersionVorschauDrawer` (Positionen der Version) — Zustand bleibt beim Aufrufer
 *    (`event-detail.tsx`, `vorschauVersion`), damit es weiterhin nur EINE Drawer-Instanz gibt.
 *  - Der Kopf-Link „Angebotsweg im Detail" öffnet den bestehenden `AngebotsVerlaufDrawer`
 *    (`ui/AngebotsVerlauf.tsx`, unverändert) — diese Karte ersetzt ihn nicht, sie verlinkt nur.
 *  - Das Zeitformat ist überall gleich ("TT.MM.JJJJ · HH:MM Uhr", Akzeptanzkriterium 1 aus
 *    Antoines Screenshot-Beleg 07.09.2026) — `formatVerlaufZeitpunkt` unten, bewusst NICHT
 *    `formatDate`/`formatDateTime` aus `ui/format.ts` (die produzieren ein Komma statt „·" bzw.
 *    lassen die Uhrzeit optional aus).
 *
 * ── Mail-Zeilen: die reichere Darstellung bleibt erhalten (Bughunt 28.08.2026) ─────────────────
 * `leiteAuftragsVerlaufAb` verschmilzt eine ausgehende Mail NUR dann mit einer Versions-Zeile,
 * wenn sie zeitlich zu einem Versand passt (Kriterium 2) — jede andere Mail bleibt eine
 * eigenständige `art: "mail"`-Zeile mit `emailId`. Für GENAU diese unabhängigen Mail-Zeilen gilt
 * weiterhin das in `test/mail-aktivitaet-verdichtung.test.tsx` belegte Verhalten: Erfolg/Fehlschlag
 * per Icon UND Text unterscheidbar, Kurzursache vorn + Rohtext unter „Technische Details"
 * (`MailFehlerHinweis`), wiederholte Fehlschläge zu einer Aufklapp-Zeile verdichtet
 * (`gruppiereWiederholteVersuche`, dasselbe Werkzeug wie `mail-protokoll.tsx`). Reine
 * Statuszeilen/Versions-Zeilen/etc. bleiben bei der einfachen Darstellung (`ZeilenInhalt`).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState, Icon, LoadingRows, MailFehlerHinweis, StatusPill, ButtonRouterLink, Card } from "../components";
import { AngebotsVerlaufDrawer } from "../AngebotsVerlauf";
import { emailStatusMeta, formatDate, formatMailEreignisse, type MailEreignis, gruppiereWiederholteVersuche, istMailFehlgeschlagen } from "../format";
import { angebotPfad, posteingangPfad } from "../../lib/pfade";
import { useMitHerkunft } from "../../lib/rueckweg";
import { verlaufZiel, type AuftragVerlaufEintrag } from "../../lib/auftrag-verlauf";
import { GRUEN_TEXT } from "../../lib/zahlungsstand";

const CARD_TITLE = "font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface";

// KI-Klassifikation eingehender Mails (Welle 3/5b) - rein informativ, keine Automatisierung dahinter.
const MAIL_CLASSIFICATION_LABEL: Record<string, string> = {
  accepted: "Zusage",
  declined: "Absage",
  question: "Frage",
  other: "Sonstiges",
};

/** Volle Mail-Zeile (`GET /api/events/:id/emails`) — dieselbe Form wie `EmailRow` in
 *  `pages/event-detail.tsx`, hier eigenständig deklariert (keine Seiten-Importe in `ui/*`). */
export interface AuftragVerlaufEmailRow {
  id: string;
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  bodyText: string | null;
  status: string | null;
  error: string | null;
  aiClassification: string | null;
  receivedAt: string;
  /** Punkt 114: Brevo-Ereignisse (zugestellt/geöffnet/geklickt …). */
  ereignisse?: MailEreignis[];
}

/** "08.09.2026 · 14:30 Uhr" — durchgehend gleiches Format, immer mit Uhrzeit (Kriterium 1). */
function formatVerlaufZeitpunkt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  const datum = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const uhrzeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${datum} · ${uhrzeit} Uhr`;
}

function ZeilenInhalt({ eintrag }: { eintrag: AuftragVerlaufEintrag }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <Icon name={eintrag.icon} className={`mt-0.5 shrink-0 text-[18px] ${eintrag.erfolg ? GRUEN_TEXT : "text-secondary dark:text-secondary-fixed-dim"}`} />
      <div className="min-w-0">
        <p className="font-body-sm text-body-sm font-semibold text-on-background [overflow-wrap:anywhere] dark:text-inverse-on-surface">{eintrag.titel}</p>
        <p className="font-body-sm text-body-sm text-secondary [overflow-wrap:anywhere] dark:text-secondary-fixed-dim">
          {formatVerlaufZeitpunkt(eintrag.zeitpunkt)}
          {eintrag.detail ? ` · ${eintrag.detail}` : ""}
          {eintrag.art === "version" && eintrag.hatPositionen === false ? " · nur Zusammenfassung" : ""}
        </p>
      </div>
    </div>
  );
}

/** Eine unabhängige Mail-Zeile (Bughunt 28.08.2026) — identisches Markup/Verhalten wie die
 *  ehemalige `EmailEntryItem` in `event-detail.tsx`, nur hier verortet. */
function MailEintragZeile({ eventId, email }: { eventId: string; email: AuftragVerlaufEmailRow }) {
  const failed = istMailFehlgeschlagen(email.status);
  return (
    <li className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
      <div className="flex items-center justify-between gap-2">
        {/* min-w-0 + truncate: Absender/Empfänger kann eine lange E-Mail-Adresse sein und darf
            nicht das Datum rechts aus dem Viewport drängen (Bughunt 28.09.2026, 390px-Überbreite,
            dasselbe Muster wie die Listenzeile in `pages/posteingang.tsx`). */}
        <span className="flex min-w-0 items-center gap-1.5 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
          <Icon name={email.direction === "inbound" ? "call_received" : "call_made"} className="shrink-0 text-[16px] text-primary" />
          <span className="truncate">{email.direction === "outbound" ? `an ${email.toAddr ?? "?"}` : email.fromAddr}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {email.aiClassification && (
            <span className="rounded-full bg-secondary/10 px-2 py-0.5 font-label-md text-xs font-medium text-secondary dark:text-secondary-fixed-dim">
              {MAIL_CLASSIFICATION_LABEL[email.aiClassification] ?? email.aiClassification}
            </span>
          )}
          {(() => {
            const meta = emailStatusMeta(email.status);
            return meta ? <StatusPill meta={meta} icon={meta.icon} /> : null;
          })()}
          <span className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{formatDate(email.receivedAt)}</span>
        </span>
      </div>
      {email.subject && <p className="mt-1 font-body-sm text-body-sm font-medium text-on-surface dark:text-inverse-on-surface">{email.subject}</p>}
      {email.error && <MailFehlerHinweis rawError={email.error} />}
      {formatMailEreignisse(email.ereignisse) && (
        <p data-testid={`mail-ereignisse-${email.id}`} className="mt-1 flex items-center gap-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          <Icon name="timeline" className="text-[14px]" />
          {formatMailEreignisse(email.ereignisse)}
        </p>
      )}
      {email.bodyText && <p className="mt-1 line-clamp-3 whitespace-pre-wrap font-body-sm text-body-sm text-on-surface-variant">{email.bodyText}</p>}
      {/* A4 (13.09.2026): auch eine Mail-Zeile fuehrt jetzt irgendwohin — in den Posteingang, wo
          BEIDE Richtungen dieses Vorgangs in einem Thread liegen. Bewusst ein eigener `<Link>` am
          Fuss der Zeile statt der ganzen Zeile als Link: diese Zeile enthaelt bereits eigene
          Links/Knoepfe (Fehlschlag-Wiederherstellung unten), und interaktive Elemente duerfen
          nicht ineinander verschachtelt werden. Ein „ganze Zeile ist ein Link"-Muster
          (Stretched Link) gehoert ins Design-System und ist Einheit E, nicht diese hier. */}
      <Link
        to={posteingangPfad(eventId)}
        data-testid={`verlauf-mail-link-${email.id}`}
        className="mt-1.5 inline-flex items-center gap-1 font-label-md text-[11.5px] font-semibold text-primary hover:underline"
      >
        <Icon name="forum" className="text-[14px]" /> Im Posteingang öffnen
      </Link>
      {failed && (
        <div className="mt-2.5 flex items-center gap-2 border-t border-outline-variant/30 pt-2.5 dark:border-outline/30">
          <ButtonRouterLink to="/einstellungen" variant="secondary" className="!py-1.5 !text-[11.5px]">
            <Icon name="link" className="text-[14px]" /> Konto verbinden
          </ButtonRouterLink>
          <ButtonRouterLink to={angebotPfad(eventId)} variant="secondary" className="!py-1.5 !text-[11.5px]">
            <Icon name="refresh" className="text-[14px]" /> Erneut senden
          </ButtonRouterLink>
        </div>
      )}
    </li>
  );
}

export function AuftragVerlaufKarte({
  eventId,
  loading,
  eintraege,
  emails,
  onVersionKlick,
  onEckdatenKlick,
}: {
  eventId: string;
  loading: boolean;
  eintraege: AuftragVerlaufEintrag[];
  /** Volle Mail-Zeilen (`GET /api/events/:id/emails`) — nur für die reichere Darstellung der
   *  UNABHÄNGIGEN `mail`-Zeilen gebraucht (Zustellstatus, Fehlertext, Retry-Bündelung). */
  emails: AuftragVerlaufEmailRow[];
  /** Öffnet die vorhandene Read-Only-Vorschau der Version (`VersionVorschauDrawer` in `event-detail.tsx`). */
  onVersionKlick: (version: number) => void;
  /** PR4 (`docs/PLAN-EVENT-FELD-VERSIONIERUNG.md` Abschnitt 4.1 Punkt 3): öffnet den
   *  `FeldVersionenDrawer` — Klick auf die verdichtete Eckdaten-Sammelzeile (`art: "eckdaten"`). */
  onEckdatenKlick: () => void;
}) {
  const [angebotswegOffen, setAngebotswegOffen] = useState(false);
  const mitHier = useMitHerkunft(); // Punkt 115: Mail-Protokoll zeigt „← Zurück“ zum Vorgang
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(new Set());

  const emailById = useMemo(() => new Map(emails.map((e) => [e.id, e])), [emails]);

  /** Nur die unabhängigen `mail`-Zeilen (nicht in eine Version verschmolzen), in derselben
   *  Reihenfolge wie `eintraege` — Grundlage fuer die Retry-Buendelung (G6). Adjazenz WIRKT
   *  bewusst nur innerhalb dieser gefilterten Teilfolge, nicht in der vollen, durchmischten Liste
   *  (robuster gegenueber dazwischenliegenden Nicht-Mail-Ereignissen als die alte Implementierung). */
  const mailGruppen = useMemo(() => {
    const rows = eintraege
      .filter((e) => e.art === "mail" && e.emailId)
      .map((e) => emailById.get(e.emailId!))
      .filter((e): e is AuftragVerlaufEmailRow => e !== undefined);
    return gruppiereWiederholteVersuche(
      rows,
      (r) => r.id,
      (r) => (r.direction === "outbound" && r.subject ? r.subject : null),
    );
  }, [eintraege, emailById]);
  const gruppeVonPrimaryId = useMemo(() => new Map(mailGruppen.map((g) => [g.primary.id, g])), [mailGruppen]);
  const alsExtraVerbraucht = useMemo(() => new Set(mailGruppen.flatMap((g) => g.extra.map((e) => e.id))), [mailGruppen]);

  function toggle(key: string) {
    setAufgeklappt((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <Card as="section" className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className={CARD_TITLE}>Verlauf</h3>
          <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            Anfrage, Angebotsversionen, Änderungswünsche, Zahlungen und Status in einem Feed
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAngebotswegOffen(true)}
            className="flex items-center gap-1 font-label-md text-[11px] font-semibold text-primary hover:underline"
          >
            <Icon name="open_in_new" className="text-[14px]" />
            Angebotsweg im Detail
          </button>
          <Link
            to={mitHier(`/mail-protokoll?eventId=${eventId}`)}
            className="flex items-center gap-1 font-label-md text-[11px] font-semibold text-primary hover:underline"
          >
            <Icon name="outgoing_mail" className="text-[14px]" />
            Mail-Protokoll
          </Link>
        </div>
      </div>

      {loading ? (
        <LoadingRows rows={3} />
      ) : eintraege.length === 0 ? (
        <EmptyState icon="history" title="Noch keine Einträge" hint="Anfrage, Angebot und Statuswechsel dieses Vorgangs erscheinen hier automatisch." />
      ) : (
        <ul className="space-y-2">
          {eintraege.map((eintrag) => {
            if (eintrag.art === "version" && typeof eintrag.version === "number") {
              return (
                <li key={eintrag.key}>
                  <button
                    type="button"
                    data-testid={`verlauf-version-${eintrag.version}`}
                    className="flex w-full items-start justify-between gap-3 rounded-lg border border-outline-variant/40 p-3 text-left transition-colors hover:bg-surface-container dark:border-outline/30 dark:hover:bg-white/5"
                    onClick={() => onVersionKlick(eintrag.version!)}
                    aria-label={`Version ${eintrag.version} ansehen`}
                  >
                    <ZeilenInhalt eintrag={eintrag} />
                    <Icon name="chevron_right" className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/70" />
                  </button>
                </li>
              );
            }

            // PR4 (Abschnitt 4.1 Punkt 3): verdichtete Eckdaten-Sammelzeile — exakt dasselbe
            // Muster wie die Versions-Zeile oben (Button + chevron_right), oeffnet aber den
            // `FeldVersionenDrawer` statt `VersionVorschauDrawer`.
            if (eintrag.art === "eckdaten") {
              return (
                <li key={eintrag.key}>
                  <button
                    type="button"
                    data-testid="verlauf-eckdaten-sammelzeile"
                    className="flex w-full items-start justify-between gap-3 rounded-lg border border-outline-variant/40 p-3 text-left transition-colors hover:bg-surface-container dark:border-outline/30 dark:hover:bg-white/5"
                    onClick={onEckdatenKlick}
                    aria-label={`${eintrag.titel} ansehen`}
                  >
                    <ZeilenInhalt eintrag={eintrag} />
                    <Icon name="chevron_right" className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/70" />
                  </button>
                </li>
              );
            }

            if (eintrag.art === "mail" && eintrag.emailId) {
              // Extra-Zeilen einer Gruppe erscheinen NUR eingeklappt unter ihrer Primärzeile,
              // nie an ihrer eigenen Position in der Liste (identisch zur alten Aktivität-Karte).
              if (alsExtraVerbraucht.has(eintrag.emailId)) return null;
              const gruppe = gruppeVonPrimaryId.get(eintrag.emailId);
              if (!gruppe) {
                const email = emailById.get(eintrag.emailId);
                return email ? <MailEintragZeile key={eintrag.key} eventId={eventId} email={email} /> : null;
              }
              const expanded = aufgeklappt.has(gruppe.key);
              return (
                <li key={eintrag.key} className="space-y-2">
                  <ul className="space-y-2">
                    <MailEintragZeile eventId={eventId} email={gruppe.primary} />
                    {gruppe.extra.length > 0 && !expanded && (
                      <li>
                        <button
                          type="button"
                          onClick={() => toggle(gruppe.key)}
                          className="ml-1 font-label-md text-[11.5px] font-semibold text-primary hover:underline"
                        >
                          {`${gruppe.extra.length} weitere${
                            gruppe.extra.every((e) => istMailFehlgeschlagen(e.status)) ? " fehlgeschlagene" : ""
                          } Zustellung${gruppe.extra.length === 1 ? "" : "en"} · Einzeln anzeigen`}
                        </button>
                      </li>
                    )}
                    {gruppe.extra.length > 0 &&
                      expanded &&
                      gruppe.extra.map((extra) => <MailEintragZeile key={extra.id} eventId={eventId} email={extra} />)}
                    {gruppe.extra.length > 0 && expanded && (
                      <li>
                        <button
                          type="button"
                          onClick={() => toggle(gruppe.key)}
                          className="ml-1 font-label-md text-[11.5px] font-semibold text-secondary hover:underline dark:text-secondary-fixed-dim"
                        >
                          Einblenden schließen
                        </button>
                      </li>
                    )}
                  </ul>
                </li>
              );
            }

            // A4: Rechnung/Zahlung/Kostenübernahme/Nachrichten-Zuordnung haben ein echtes Ziel
            // (`verlaufZiel`, rein + getestet) — und zwar als `<Link>`, nicht als
            // `onClick`+`navigate`: Cmd-/Strg-/Mittelklick muss einen neuen Tab öffnen (Antoine,
            // 13.09.2026). Zeilen ohne Ziel bleiben unverändert nicht klickbar.
            const ziel = verlaufZiel(eintrag, eventId);
            if (ziel) {
              return (
                <li key={eintrag.key}>
                  <Link
                    to={ziel.href}
                    aria-label={`${eintrag.titel} — ${ziel.label}`}
                    data-testid={`verlauf-ziel-${eintrag.key}`}
                    className="flex w-full items-start justify-between gap-3 rounded-lg border border-outline-variant/40 p-3 text-left transition-colors hover:bg-surface-container dark:border-outline/30 dark:hover:bg-white/5"
                  >
                    <ZeilenInhalt eintrag={eintrag} />
                    <Icon name="chevron_right" className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/70" />
                  </Link>
                </li>
              );
            }

            return (
              <li key={eintrag.key} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                <ZeilenInhalt eintrag={eintrag} />
              </li>
            );
          })}
        </ul>
      )}

      <AngebotsVerlaufDrawer eventId={eventId} open={angebotswegOffen} onClose={() => setAngebotswegOffen(false)} />
    </Card>
  );
}
